import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  CREATE_LEARNING_PLAN,
  CREATE_LEARNING_PLAN_API,
  DELETE,
  DELETE_USER_OR_GROUP_LEARNING_PLAN,
  DELETE_USER_OR_GROUP_LEARNING_PLAN_API,
  GET,
  GET_LEARNING_PLAN_BY_DOMAIN,
  GET_LEARNING_PLAN_BY_DOMAIN_API,
  GET_LEARNING_PLAN_BY_DOMAIN_ID,
  GET_LEARNING_PLAN_BY_DOMAIN_ID_API,
  GET_LEARNING_PLAN_BY_ID,
  GET_LEARNING_PLAN_BY_ID_API,
  GET_LEARNING_PLAN_BY_USER_ID,
  GET_LEARNING_PLAN_BY_USER_ID_API,
  GET_LEARNING_PLAN_DETAILS_BY_ID,
  GET_LEARNING_PLAN_DETAILS_BY_ID_API,
  GET_LEARNING_PLAN_DETAILS_BY_TAB,
  GET_LEARNING_PLAN_DETAILS_BY_TAB_API,
  MODIFY_TARGET_DATE_LEARNING_PLAN,
  MODIFY_TARGET_DATE_LEARNING_PLAN_API,
  POST,
  PUT,
  UPDATE_LEARNING_PLAN,
} from "../../Api";
import apiRequest from "../../Api/connector";
import { STATUS_200 } from "../../Api/constants";
import {
  LearningPlan,
  LearningPlanDetails,
  LearningPlanDomainResponse,
  LearningPlanPayload,
  LearningPlansByDomainResponse,
} from "../../Api/entities/LearningPlanMasterEntity";
import { SomethingWentWrong } from "../../utils/Constant";
import { showToast } from "../../utils/helper/helper";

interface LearningPlanMasterInterface {
  learningPlanById: LearningPlan;
  learningPlanByUserId: any;
  learningPlanDetailsByTab: any;
  learningPlanDetailsById: LearningPlanDetails;
  learningPlanByDomainId: LearningPlansByDomainResponse;
  learningPlanByDomain: LearningPlanDomainResponse;
  numberLevel: number;
  loading: boolean;
  error: string;
  showFinish: boolean;
}

const initialState: LearningPlanMasterInterface = {
  learningPlanByUserId: {},
  learningPlanDetailsById: null,
  learningPlanDetailsByTab: null,
  learningPlanById: null,
  learningPlanByDomainId: null,
  learningPlanByDomain: {
    learning_plan_domains: [],
  },
  numberLevel: 1,
  loading: false,
  error: null,
  showFinish: false,
};

export const createLearningPlan = createAsyncThunk(
  CREATE_LEARNING_PLAN,
  async ({ payload }: { payload: LearningPlanPayload | any }) => {
    const response = await apiRequest(
      POST,
      CREATE_LEARNING_PLAN_API(),
      payload
    );
    return response;
  }
);

export const updateLearningPlan = createAsyncThunk(
  UPDATE_LEARNING_PLAN,
  async ({ payload, type }: { payload: any; type?: string }) => {
    const response = await apiRequest(
      PUT,
      CREATE_LEARNING_PLAN_API(type),
      payload
    );
    return response;
  }
);

export const getLearningPlanByDomain = createAsyncThunk(
  GET_LEARNING_PLAN_BY_DOMAIN,
  async () => {
    const response = await apiRequest(GET, GET_LEARNING_PLAN_BY_DOMAIN_API());
    return response.data;
  }
);

export const getLearningPlanById = createAsyncThunk(
  GET_LEARNING_PLAN_BY_ID,
  async ({ id }: { id: string }) => {
    const response = await apiRequest(GET, GET_LEARNING_PLAN_BY_ID_API(id));
    return response.data;
  }
);

export const getLearningPlanByUserId = createAsyncThunk(
  GET_LEARNING_PLAN_BY_USER_ID,
  async ({
    id,
    year,
    domain,
    role,
    level,
  }: {
    id: string;
    year?: string;
    domain?: string;
    role?: string;
    level?: string;
  }) => {
    const response = await apiRequest(
      GET,
      GET_LEARNING_PLAN_BY_USER_ID_API(id, year, domain, role, level)
    );
    return response.data;
  }
);

export const getLearningPlanDetailsByTab = createAsyncThunk(
  GET_LEARNING_PLAN_DETAILS_BY_TAB,
  async ({ id, tab, userId }: { id: string; tab: string; userId?: any }) => {
    const response = await apiRequest(
      GET,
      GET_LEARNING_PLAN_DETAILS_BY_TAB_API(id, tab, userId)
    );
    return response.data;
  }
);

export const getLearningPlanDetailsById = createAsyncThunk(
  GET_LEARNING_PLAN_DETAILS_BY_ID,
  async ({ id, user_id }: { id: string; user_id?: string }) => {
    const response = await apiRequest(
      GET,
      GET_LEARNING_PLAN_DETAILS_BY_ID_API(id, user_id)
    );
    return response.data;
  }
);

export const deleteUserOrGroupLearningPlan = createAsyncThunk(
  DELETE_USER_OR_GROUP_LEARNING_PLAN,
  async ({ id, type, payload }: { id: any; type: string; payload: any }) => {
    const response = await apiRequest(
      DELETE,
      DELETE_USER_OR_GROUP_LEARNING_PLAN_API(id, type),
      payload
    );
    return response?.data;
  }
);

export const modifyTargetDateLearningPlan = createAsyncThunk(
  MODIFY_TARGET_DATE_LEARNING_PLAN,
  async ({ type, payload }: { type: string; payload: any }) => {
    const response = await apiRequest(
      PUT,
      MODIFY_TARGET_DATE_LEARNING_PLAN_API(type),
      payload
    );
    return response?.data;
  }
);

export const getLearningPlanByDomainId = createAsyncThunk(
  GET_LEARNING_PLAN_BY_DOMAIN_ID,
  async ({
    domain_id,
    status,
    role,
    level,
  }: {
    domain_id: string;
    status: string;
    role?: any;
    level?: any;
  }) => {
    const response = await apiRequest(
      GET,
      GET_LEARNING_PLAN_BY_DOMAIN_ID_API(domain_id, status, role, level)
    );
    return response.data;
  }
);

const LearningPlanMasterSlice = createSlice({
  name: "assessmentCatlog",
  initialState,
  reducers: {
    setLearningPlanMasterToInitials: state => {
      state.learningPlanById = initialState.learningPlanById;
      state.numberLevel = initialState.numberLevel;
      state.showFinish = initialState.showFinish;
    },
    setShowFinish: (state, action) => {
      state.showFinish = action.payload;
    },
    setNumberLevelForSteps: (state, action) => {
      state.numberLevel = action.payload;
    },
    handleBackButton: state => {
      state.showFinish = false;
      if (state.numberLevel === 2) {
        state.numberLevel = state.numberLevel - 1;
      }
    },
    handlePlanNextButton: state => {
      if (state.numberLevel === 1) {
        state.showFinish = true;
        state.numberLevel = 2;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createLearningPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLearningPlan.fulfilled, (state, action) => {
        state.loading = false;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(createLearningPlan.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(deleteUserOrGroupLearningPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserOrGroupLearningPlan.fulfilled, (state, action) => {
        state.loading = false;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(deleteUserOrGroupLearningPlan.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(modifyTargetDateLearningPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(modifyTargetDateLearningPlan.fulfilled, (state, action) => {
        state.loading = false;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(modifyTargetDateLearningPlan.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(updateLearningPlan.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLearningPlan.fulfilled, (state, action) => {
        state.loading = false;
        const { status_code, message } = action.payload;
        if (status_code === STATUS_200) showToast(message, "success");
      })
      .addCase(updateLearningPlan.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getLearningPlanByDomain.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getLearningPlanByDomain.fulfilled,
        (state, action: PayloadAction<LearningPlanDomainResponse>) => {
          state.loading = false;
          state.learningPlanByDomain = action.payload;
        }
      )
      .addCase(getLearningPlanByDomain.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getLearningPlanById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getLearningPlanById.fulfilled,
        (state, action: PayloadAction<LearningPlan>) => {
          state.loading = false;
          state.learningPlanById = action.payload;
        }
      )
      .addCase(getLearningPlanById.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getLearningPlanByUserId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getLearningPlanByUserId.fulfilled,
        (state, action: PayloadAction<LearningPlan>) => {
          state.loading = false;
          state.learningPlanByUserId = action.payload;
        }
      )
      .addCase(getLearningPlanByUserId.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getLearningPlanDetailsByTab.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLearningPlanDetailsByTab.fulfilled, (state, action: any) => {
        state.loading = false;
        state.learningPlanDetailsByTab = action.payload;
      })
      .addCase(getLearningPlanDetailsByTab.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getLearningPlanDetailsById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLearningPlanDetailsById.fulfilled, (state, action: any) => {
        state.loading = false;
        state.learningPlanDetailsById = action.payload;
      })
      .addCase(getLearningPlanDetailsById.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });

    builder
      .addCase(getLearningPlanByDomainId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getLearningPlanByDomainId.fulfilled,
        (state, action: PayloadAction<LearningPlansByDomainResponse>) => {
          state.loading = false;
          state.learningPlanByDomainId = action.payload;
        }
      )
      .addCase(getLearningPlanByDomainId.rejected, (state, action) => {
        try {
          const error = JSON.parse(action.error.message);
          state.error = error.message;
          showToast(state?.error, "error");
        } catch (e) {
          state.error = SomethingWentWrong;
        }
      });
  },
});

export const {
  setLearningPlanMasterToInitials,
  setShowFinish,
  handleBackButton,
  handlePlanNextButton,
  setNumberLevelForSteps,
} = LearningPlanMasterSlice.actions;
export default LearningPlanMasterSlice.reducer;
